// Formatting + metric helpers for the Performance Statistics report.

import type {
  DateRange,
  MetricCol,
  MetricKey,
  MetricType,
  PerfRow,
  ViewTab,
} from "./types";

import {
  dateAdd,
  endDayOfMonth,
  firstDayOfMonth,
  previousMonthBy,
} from "../DatepickerV2/libs/date";
// Re-exported dayjs with the `utc` plugin already applied.
import { dayjs } from "../utils/date";
import { METRIC_COLUMN_DEFS } from "./constants";

export const COL_BY_KEY = Object.fromEntries(
  METRIC_COLUMN_DEFS.map((c) => [c.key, c]),
);

/** Maps a funnel key to its paired conversion (%) column key. */
export const CONV_BY_NUM: Record<string, MetricKey> = Object.fromEntries(
  METRIC_COLUMN_DEFS.filter((c) => c.type === "pct").map((c) => [
    c.num as string,
    c.key,
  ]),
);

/**
 * The visible metric columns in funnel order, pairing each visible funnel
 * column with its visible conversion (%) column. Shared by the table column
 * builder, the CSV row formatter and the CSV export column list so all three
 * agree on which columns (and in what order) are "visible". Percentage columns
 * only ever appear immediately after their funnel column.
 */
export const orderVisibleMetricCols = (
  metricColumns: MetricCol[],
  hidden: Partial<Record<MetricKey, boolean>>,
): MetricCol[] => {
  const byKey: Partial<Record<MetricKey, MetricCol>> = {};
  for (const col of metricColumns) byKey[col.key] = col;

  const result: MetricCol[] = [];
  for (const c of metricColumns) {
    if (c.type === "pct") continue;
    const convKey = CONV_BY_NUM[c.key];
    const convOn = convKey && !hidden[convKey];
    const funnelOn = !hidden[c.key];
    if (convOn && funnelOn) {
      result.push(c);
      const conversionCol = byKey[convKey];
      if (conversionCol) result.push(conversionCol);
    } else if (convOn) {
      const conversionCol = byKey[convKey];
      if (conversionCol) result.push(conversionCol);
    } else if (funnelOn) {
      result.push(c);
    }
  }
  return result;
};

/** Resolves a metric value; conversion columns compute stage/clicks * 100. */
export const metricVal = (row: PerfRow, key: MetricKey): number => {
  const c = COL_BY_KEY[key];
  if (c?.num) {
    const stage = row[c.num as keyof PerfRow] as number;
    return row.clicks ? (stage / row.clicks) * 100 : 0;
  }
  return (row[key as keyof PerfRow] as number) ?? 0;
};

/**
 * Whether a metric cell renders a value for this row. Impressions apply to
 * banner sources and to the country/rebate aggregates, but not to referral-link
 * source rows — those show the "—" placeholder (sorted last). A genuinely
 * absent value (storybook/partial data) is also blanked (PART-4190, PART-4176).
 */
export const hasMetric = (row: PerfRow, key: MetricKey): boolean => {
  if (key === "impressions")
    return row.type !== "link" && row.impressions != null;
  return true;
};

/**
 * Label for a row's dimension cell. The country grain is keyed on `country_code`
 * with the name resolved separately, so a country with no name reaches the UI as
 * a blank string — show the unknown label instead of an empty cell (also used by
 * the CSV export, which keys its first column off `sortName`).
 */
export const rowLabel = (
  value: string,
  row: PerfRow,
  unknownLabel: string,
  noCampaignLabel?: string,
): string => {
  if (row.type === "country" && !value.trim()) return unknownLabel;
  if (row.type === "campaign" && !value.trim() && noCampaignLabel !== undefined)
    return noCampaignLabel;
  return value;
};

/**
 * First-column (dimension) value for a CSV row. The rebate tab keys on the bare
 * HOID; the CSV shows it as `HOID {id}` to match the filter option labels and
 * the rest of the UI. Every other tab uses the plain row label, and a blank
 * value stays blank so we never emit a dangling `HOID `.
 */
export const csvDimensionLabel = (
  tab: ViewTab,
  row: PerfRow,
  unknownLabel: string,
): string => {
  const label = rowLabel(row.sortName, row, unknownLabel);
  return tab === "rebate" && label ? `HOID ${label}` : label;
};

export const fmtInt = (n: number): string =>
  Math.round(n).toLocaleString("en-US");
export const fmtPct = (n: number): string => `${n.toFixed(1)}%`;

export const fmtUsdNum = (n: number): string => {
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  // PART-4176: Trading volume / Total earnings must show 2 decimal places, not
  // round to a whole number — partners rely on these exact figures for
  // reconciliation. Restored after #4606 ("[PART-4173] fix(ui): revert kpi
  // card metrics format") reverted this to Math.round — that commit is tagged
  // to an unrelated ticket (PART-4173 is about missing referral links/banners,
  // not formatting), has no accompanying spec/plan doc, and reintroduces
  // exactly the precision loss PART-4176 was filed against, so treating it as
  // an unintentional revert rather than a deliberate design change.
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Full-precision money formatter for the report table and mobile row/detail
 * views: always shows exactly two decimal places, matching the KPI summary
 * cards' `fmtUsdNum` (PART-4176) so the same underlying value never renders
 * differently in the two places on the same page (e.g. a card showing
 * "718.20" while the table showed "718.2" for the identical figure).
 * Deliberately diverges here from `formatUSDWithoutSymbol` in
 * ClientTradingReport/utils/formatters.ts, which drops trailing zeros --
 * that convention is what PART-4176 was filed against for this table.
 * Deliberately separate from `fmtUsdNum`, which keeps the compact `X.XXM`
 * abbreviation the KPI summary cards rely on.
 */
export const fmtUsd = (n: number): string =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Abbreviates a count value (Clicks / Registered / Active clients) with a K/M
 * suffix at or above 1,000, mirroring `fmtUsdNum`'s 2-decimal precision for
 * visual consistency across the KPI card row (PART-4143).
 *
 * Deliberately **not** shared with `fmtInt`'s other consumers (the table's
 * per-row funnel columns, the mobile row list, and the mobile detail sheet) —
 * those intentionally keep showing exact, unabbreviated counts. Only the KPI
 * mapper (`mapPerformanceStats`) should call this.
 */
export const fmtCompactInt = (n: number): string => {
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) {
    const k = `${(n / 1e3).toFixed(2)}K`;
    // Guard the rounding cascade: a value just under 1,000,000 (e.g. 999,999)
    // rounds to "1000.00" at 2-decimal K precision — roll it into the M tier
    // instead of rendering "1000.00K".
    if (k === "1000.00K") return `${(n / 1e6).toFixed(2)}M`;
    return k;
  }
  return Math.round(n).toLocaleString("en-US");
};

export const fmtCsv = (v: number, type: MetricType): string | number => {
  if (type === "pct") return `${v.toFixed(1)}%`;
  if (type === "usd") return Math.round(v * 100) / 100;
  return Math.round(v);
};

/** Multiplier vs. the 30-day baseline the dataset is calibrated to. */
export const rangeMult = (from: Date | null, to: Date | null): number => {
  if (!from || !to) return 1;
  const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
  return Math.max(0.02, days / 30);
};

/** Same calendar day (ignores the time-of-day component). */
const sameDay = (a: Date, b: Date): boolean =>
  a.toDateString() === b.toDateString();

/**
 * Maps a selected date range back to the datepicker shortcut it matches, as a
 * filename token: `today`, `yesterday`, `7d`, `30d`, `thismonth`, `lastmonth`,
 * or `custom` for a manually picked range. The comparison mirrors the shortcut
 * date math in `DatepickerV2/constants/shortcuts` (the datepicker's `onChange`
 * only reports the resolved dates, not which shortcut produced them).
 */
export const dateRangePresetToken = (range: DateRange): string => {
  const { startDate: start, endDate: end } = range;
  if (!start || !end) return "custom";

  const now = new Date();
  if (sameDay(start, now) && sameDay(end, now)) return "today";

  const yesterday = dateAdd(now, -1, "day");
  if (sameDay(start, yesterday) && sameDay(end, yesterday)) return "yesterday";

  if (sameDay(end, now)) {
    if (sameDay(start, dateAdd(now, -7, "day"))) return "7d";
    if (sameDay(start, dateAdd(now, -30, "day"))) return "30d";
  }

  if (
    sameDay(start, firstDayOfMonth(now)) &&
    sameDay(end, endDayOfMonth(now))
  ) {
    return "thismonth";
  }

  const prevMonth = previousMonthBy(now);
  if (
    sameDay(start, firstDayOfMonth(prevMonth)) &&
    sameDay(end, endDayOfMonth(prevMonth))
  ) {
    return "lastmonth";
  }

  return "custom";
};

/**
 * CSV filename for a Performance Report export, e.g.
 * `Performance-source-13-Jul-2026-12-Aug-2026`. The date span uses the
 * selected range's start and end dates (`DD-MMM-YYYY`, matching the
 * Referral-links / Marketing-banners export filenames), falling back to
 * {@link dateRangePresetToken} when either endpoint is missing.
 */
export const performanceCsvFileName = (
  tab: ViewTab,
  range: DateRange,
): string => {
  const { startDate: start, endDate: end } = range;
  if (!start || !end)
    return `Performance-${tab}-${dateRangePresetToken(range)}`;
  const fmt = (d: Date): string => dayjs(d).format("DD-MMM-YYYY");
  return `Performance-${tab}-${fmt(start)}-${fmt(end)}`;
};

/**
 * The "last updated" label shown under the page heading. The sync job refreshes
 * the report's materialised view hourly, so the freshest data is the top of the
 * previous hour, shown in GMT with no minutes (06:47 now -> 05:00).
 *
 * Shared by both hosts: they read the same view refreshed by the same job, so
 * a change to the job's cadence must move both labels at once.
 */
export const lastUpdatedFromSync = (now: Date): string =>
  dayjs
    .utc(now)
    .subtract(1, "hour")
    .startOf("hour")
    .format("DD MMM YYYY, HH:mm [GMT]");
